import { type RequestContext } from '~/types/RequestContext';

import { BettingInsightsComponent } from '../BettingInsightsConfiguration';

import {
	BettingInsightsMain,
	type BettingInsightsMainConfig,
} from './BettingInsightsMain';
import {
	BettingInsightsTableCaption,
	type BettingInsightsTableCaptionConfig,
} from './BettingInsightsTableCaption';
import {
	BettingInsightsTableCell,
	type BettingInsightsTableCellConfig,
} from './BettingInsightsTableCell';
import {
	BettingInsightsTableFooter,
	type BettingInsightsTableFooterConfig,
} from './BettingInsightsTableFooter';
import {
	BettingInsightsTableHeading,
	type BettingInsightsTableHeadingConfig,
} from './BettingInsightsTableHeading';

export interface BettingInsightsTableConfig<TResult, TContent> {
	caption?: BettingInsightsTableCaptionConfig<TContent>;
	cells: BettingInsightsTableCellConfig<TContent>[];
	footer?: BettingInsightsTableFooterConfig<TContent>;
	getContent(
		this: BettingInsightsTable<TResult, TContent>,
		data: { result: TResult },
	): TContent[];
	headings: BettingInsightsTableHeadingConfig<TContent>[];
	main?: BettingInsightsMainConfig<TContent>;
	shouldToggle?: boolean;
	showAdditionalTitle?: boolean;
}

export class BettingInsightsTable<TResult, TContent>
	extends BettingInsightsComponent
	implements BettingInsightsTableConfig<TResult, TContent>
{
	caption?: BettingInsightsTableCaption<TContent>;
	cells: BettingInsightsTableCell<TContent>[];
	footer?: BettingInsightsTableFooter<TContent>;
	getContent: (data: { result: TResult }) => TContent[];
	headings: BettingInsightsTableHeading<TContent>[];
	main?: BettingInsightsMain<TContent>;
	shouldToggle: boolean;
	showAdditionalTitle: boolean;

	constructor({
		caption,
		cells,
		footer,
		getContent,
		headings,
		main,
		shouldToggle,
		showAdditionalTitle,
		...requestContext
	}: BettingInsightsTableConfig<TResult, TContent> & RequestContext) {
		super(requestContext);
		this.caption = caption
			? BettingInsightsTableCaption.withRequestContext<TContent>(
					requestContext,
					caption,
				)
			: undefined;
		this.cells = cells.map(cell =>
			BettingInsightsTableCell.withRequestContext<TContent>(
				requestContext,
				cell,
			),
		);
		this.footer = footer
			? BettingInsightsTableFooter.withRequestContext<TContent>(
					requestContext,
					footer,
				)
			: undefined;
		this.getContent = getContent.bind(this);
		this.headings = headings.map(heading =>
			BettingInsightsTableHeading.withRequestContext<TContent>(
				requestContext,
				heading,
			),
		);
		this.main = main
			? BettingInsightsMain.withRequestContext<TContent>(requestContext, main)
			: undefined;
		this.shouldToggle = shouldToggle ?? false;
		this.showAdditionalTitle = showAdditionalTitle ?? false;
	}

	static withRequestContext<TResult, TContent>(
		requestContext: RequestContext,
		config: BettingInsightsTableConfig<TResult, TContent>,
	) {
		return new this<TResult, TContent>({
			...requestContext,
			...config,
		});
	}
}
